import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { editIconSm } from '@tpct/icons';
import { Tabs, TabPanel } from '@tpct/admin-ui';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import ProfileAvatar from '../../hub-shared/components/ProfileAvatar';
import { getCabinetProfileEditGate } from './gates/portalGates';
import AdvancedTab from './tabs/AdvancedTab';
import NotificationsTab from './tabs/NotificationsTab';
import PrivacyDataSection from './tabs/PrivacyDataSection';
import type { PortalBootstrapData } from './types/window';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from './utils/fetchWithNonce';
import { parseApiError } from './utils/parseApiError';
import type { PortalSearchSyncFn } from './utils/portalUrlState';
import type { MediaPayload } from '@/hub-shared/utils/mediaPayload';
import { parsePortalSearchParams } from './utils/portalUrlState';

type ProfilePageProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

type ProfileData = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  avatar_attachment_id: number | null;
  avatar_url: string;
  cabinet_name?: string;
  cabinet_logo_media?: MediaPayload | null;
  can_edit_cabinet?: boolean;
  product_updates_opt_in?: boolean;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const STRENGTH_LABELS = [
  () => __('Très faible'),
  () => __('Faible'),
  () => __('Moyen'),
  () => __('Fort'),
  () => __('Très fort'),
] as const;
const STRENGTH_CLASSES = ['very-weak', 'weak', 'medium', 'strong', 'very-strong'] as const;

function passwordMeetsPortalPolicy(password: string): boolean {
  if (password.length < 12) {
    return false;
  }
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password)) {
    return false;
  }
  if (!/[0-9]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
    return false;
  }
  const common = ['password', '123456', 'qwerty', 'letmein', 'welcome', 'admin'] as const;
  const lower = password.toLowerCase();
  return !common.some((c) => lower.includes(c));
}

function computePasswordStrength(password: string): { strength: number; label: string; className: string; feedback: string[] } {
  if (password === '') {
    return { strength: 0, label: '', className: '', feedback: [] };
  }

  const feedback: string[] = [];
  let strength = 0;

  if (password.length >= 12) {
    strength++;
  } else {
    feedback.push(__('Au moins 12 caractères'));
  }
  if (/[a-z]/.test(password)) {
    strength++;
  } else {
    feedback.push(__('Une minuscule'));
  }
  if (/[A-Z]/.test(password)) {
    strength++;
  } else {
    feedback.push(__('Une majuscule'));
  }
  if (/[0-9]/.test(password)) {
    strength++;
  } else {
    feedback.push(__('Un chiffre'));
  }
  if (/[^a-zA-Z0-9]/.test(password)) {
    strength++;
  }

  const idx = Math.max(0, strength - 1);
  const labelFn = STRENGTH_LABELS[idx];
  let label = labelFn ? labelFn() : '';
  if (feedback.length > 0 && strength < 3) {
    label += ' - ' + __('Manque: ') + feedback.join(', ');
  }

  return {
    strength,
    label,
    className: STRENGTH_CLASSES[idx] ?? '',
    feedback,
  };
}

type TransferAcceptanceSectionProps = {
  bootstrap: PortalBootstrapData;
  token: string;
  onSessionExpired: () => void;
};

type TwoFactorManagementSectionProps = {
  bootstrap: PortalBootstrapData;
};

const TwoFactorManagementSection = ({ bootstrap }: TwoFactorManagementSectionProps) => {
  const twoFactor = bootstrap.twoFactor;
  if (!twoFactor?.available) {
    return null;
  }

  return (
    <a className="btn-tertiary" href={twoFactor.manageUrl}>
        {twoFactor.configured
          ? __('Gérer l’authentification à deux facteurs')
          : __('Configurer l’authentification à deux facteurs')}
    </a>
  );
};

const TransferAcceptanceSection = ({
  bootstrap,
  token,
  onSessionExpired,
}: TransferAcceptanceSectionProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  const handleAccept = useCallback(() => {
    if (submitting || !token) return;
    setSubmitting(true);
    setError('');

    fetchWithNonce(`${bootstrap.restUrls.accountingHub}ownership-transfer/accept`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': bootstrap.nonce,
      },
      body: JSON.stringify({ token }),
    })
      .then(async (response) => {
        if (response.status === 401) {
          onSessionExpired();
          return;
        }

        if (response.ok) {
          setSuccess(true);
          setSubmitting(false);
          return;
        }

        setError(await parseApiError(response));
        setSubmitting(false);
      })
      .catch(() => {
        setError(__('L\'acceptation a échoué.'));
        setSubmitting(false);
      });
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub, onSessionExpired, submitting, token]);

  if (success) {
    return (
      <div className="user-profile-transfer-accept container-style" style={{ marginTop: '24px' }}>
        <p className="accounting-hub-notice accounting-hub-notice-success" aria-live="polite">
          {__('Propriété du cabinet transférée avec succès.')}
        </p>
      </div>
    );
  }

  return (
    <div className="user-profile-transfer-accept container-style" style={{ marginTop: '24px' }}>
      <h4>{__('Invitation au transfert de propriété')}</h4>
      <p>{__('Vous avez été invité à devenir propriétaire du cabinet. Acceptez pour finaliser le transfert.')}</p>
      {error ? <p className="accounting-hub-notice accounting-hub-notice-error">{error}</p> : null}
      <button
        type="button"
        className="btn-primary"
        disabled={submitting}
        onClick={() => void handleAccept()}
      >
        {submitting ? __('Acceptation...') : __('Accepter')}
      </button>
    </div>
  );
};

const getInitialTabFromUrl = (isOwner: boolean, hasNotificationsTab: boolean): string => {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  if (tab === 'notifications' && hasNotificationsTab) {
    return 'notifications';
  }
  if (tab === 'advanced' && isOwner) {
    return 'advanced';
  }
  return 'profile';
};

const ProfilePage = ({ bootstrap, onSessionExpired, search, patchPortalSearch }: ProfilePageProps) => {
  const portalTabs = typeof patchPortalSearch === 'function';
  const effectiveSearch = search ?? (typeof window !== 'undefined' ? window.location.search : '');

  // The server moves the emailed token out of the URL into a cookie and hands it over here (#1400).
  const transferToken = bootstrap.pendingOwnershipTransferToken;

  const isSubscriptionOwnerForTab = bootstrap.isSubscriptionOwner === true;
  const hasNotificationsTab =
    bootstrap.cabinetId !== null && bootstrap.cabinetId !== undefined && bootstrap.cabinetId > 0;
  const useProfileTabs = isSubscriptionOwnerForTab || hasNotificationsTab;

  const portalProfileTab = useMemo(() => {
    const params = parsePortalSearchParams(effectiveSearch);
    if (hasNotificationsTab && params.get('tab') === 'notifications') {
      return 'notifications' as const;
    }
    if (isSubscriptionOwnerForTab && params.get('tab') === 'advanced') {
      return 'advanced' as const;
    }
    return 'profile' as const;
  }, [effectiveSearch, hasNotificationsTab, isSubscriptionOwnerForTab]);

  const [legacyProfileTab] = useState<string>(() =>
    getInitialTabFromUrl(isSubscriptionOwnerForTab, hasNotificationsTab),
  );
  const profileTabShell = portalTabs ? portalProfileTab : legacyProfileTab;

  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState<string>('');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [nameSaving, setNameSaving] = useState<boolean>(false);
  const [nameMessage, setNameMessage] = useState<string>('');
  const [passwordSaving, setPasswordSaving] = useState<boolean>(false);
  const [passwordMessage, setPasswordMessage] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPasswordFields, setShowPasswordFields] = useState<boolean>(false);
  const [, setAvatarUploading] = useState<boolean>(false);
  const [avatarError, setAvatarError] = useState<string>('');
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cabinetName, setCabinetName] = useState<string>('');
  const [productUpdatesOptIn, setProductUpdatesOptIn] = useState<boolean>(false);

  const cabinetProfileEditGate = useMemo(
    () => getCabinetProfileEditGate({
      canEditCabinet: profile?.can_edit_cabinet,
      isCabinetOwner: bootstrap.isCabinetOwner,
    }),
    [bootstrap.isCabinetOwner, profile?.can_edit_cabinet],
  );

  const passwordStrength = useMemo(() => computePasswordStrength(newPassword), [newPassword]);
  const passwordMatch = useMemo(() => {
    if (confirmPassword === '') return { text: '', className: '' };
    if (newPassword === confirmPassword) {
      return { text: __('✓ Les mots de passe correspondent'), className: 'password-match-success' };
    }
    return { text: __('✗ Les mots de passe ne correspondent pas'), className: 'password-match-error' };
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    const didChangePassword = window.sessionStorage.getItem('acchub_profile_password_changed');
    if (didChangePassword === '1') {
      setPasswordMessage(__('Mot de passe modifié avec succès.'));
      window.sessionStorage.removeItem('acchub_profile_password_changed');
    }
  }, []);

  const hydrateProfile = (data: ProfileData) => {
    setProfile(data);
    setFirstName(data.first_name);
    setLastName(data.last_name);
    setAvatarPreviewUrl(data.avatar_url);
    setProductUpdatesOptIn(data.product_updates_opt_in === true);
    if (
      data.cabinet_name !== undefined &&
      !bootstrap.currentUser.roles.includes('customer')
    ) {
      setCabinetName(data.cabinet_name);
    }
  };

  const fetchProfile = async () => {
    setLoadState('loading');
    setLoadError('');

    try {
      const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}profile`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-WP-Nonce': bootstrap.nonce,
        },
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (!response.ok) {
        setLoadState('error');
        setLoadError(await parseApiError(response));
        return;
      }

      const payload = (await response.json()) as ProfileData;
      hydrateProfile(payload);
      setLoadState('ready');
    } catch {
      setLoadState('error');
      setLoadError(__('Impossible de charger le profil.'));
    }
  };

  const editIconUrl = editIconSm;

  if (loadState === 'idle') {
    void fetchProfile();
    return (
      <div id="custom-profile-page" aria-live="polite">
        <h2 className="h4">{__('Mon Profil')}</h2>
        <p>{__('Chargement du profil...')}</p>
      </div>
    );
  }

  if (loadState === 'loading') {
    return (
      <div id="custom-profile-page" aria-live="polite">
        <h2 className="h4">{__('Mon Profil')}</h2>
        <p>{__('Chargement du profil...')}</p>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div id="custom-profile-page" aria-live="polite">
        <h2 className="h4">{__('Mon Profil')}</h2>
        <p>{__("Le profil n'a pas pu être chargé. ")}{loadError}</p>
        <button type="button" className="button" onClick={() => void fetchProfile()}>
          {__('Réessayer')}
        </button>
      </div>
    );
  }

  const handleSaveIdentity = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNameSaving(true);
    setNameMessage('');

    try {
      const body: Record<string, string | boolean> = {
        first_name: firstName,
        last_name: lastName,
        product_updates_opt_in: productUpdatesOptIn,
      };
      if (
        profile?.can_edit_cabinet &&
        !bootstrap.currentUser.roles.includes('customer')
      ) {
        body.cabinet_name = cabinetName;
      }
      const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}profile`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': bootstrap.nonce,
        },
        body: JSON.stringify(body),
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (!response.ok) {
        setNameMessage(await parseApiError(response));
        return;
      }

      const payload = (await response.json()) as ProfileData;
      hydrateProfile(payload);
      setNameMessage(__('Profil mis à jour.'));
    } catch {
      setNameMessage(__('Impossible de mettre à jour le profil.'));
    } finally {
      setNameSaving(false);
    }
  };

  const validatePasswordSubmit = (): boolean => {
    const hasNew = newPassword !== '';
    const hasConfirm = confirmPassword !== '';
    const hasCurrent = currentPassword !== '';

    if (!hasNew && !hasConfirm && !hasCurrent) return true;

    if (!hasCurrent) {
      setPasswordError(__('Veuillez entrer votre mot de passe actuel.'));
      return false;
    }
    if (!hasNew) {
      setPasswordError(__('Veuillez entrer un nouveau mot de passe.'));
      return false;
    }
    if (!hasConfirm) {
      setPasswordError(__('Veuillez confirmer le nouveau mot de passe.'));
      return false;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(__('Les mots de passe ne correspondent pas.'));
      return false;
    }
    if (!passwordMeetsPortalPolicy(newPassword)) {
      setPasswordError(
        __(
          'Le mot de passe doit contenir au moins 12 caractères, dont des majuscules, minuscules, chiffres et caractères spéciaux.',
        ),
      );
      return false;
    }
    return true;
  };

  const handleSavePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordError('');
    if (!validatePasswordSubmit()) return;

    setPasswordSaving(true);
    setPasswordMessage('');

    try {
      const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}profile/change-password`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': bootstrap.nonce,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (!response.ok) {
        setPasswordError(await parseApiError(response));
        return;
      }

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      window.sessionStorage.setItem('acchub_profile_password_changed', '1');
      window.location.reload();
    } catch {
      setPasswordError(__('Impossible de changer le mot de passe.'));
    } finally {
      setPasswordSaving(false);
    }
  };

  const onAvatarFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    setAvatarUploading(true);
    setAvatarError('');

    const previewReader = new FileReader();
    previewReader.onload = (loadEvent) => {
      if (typeof loadEvent.target?.result === 'string') {
        setAvatarPreviewUrl(loadEvent.target.result);
      }
    };
    previewReader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}profile/avatar`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-WP-Nonce': bootstrap.nonce,
        },
        body: formData,
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (!response.ok) {
        setAvatarError(await parseApiError(response));
        setAvatarPreviewUrl(profile?.avatar_url ?? '');
        return;
      }

      await fetchProfile();
    } catch {
      setAvatarError(__('Impossible de téléverser l\'avatar.'));
      setAvatarPreviewUrl(profile?.avatar_url ?? '');
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const hasCabinet = profile && 'can_edit_cabinet' in profile;
  const showCabinetProfileFields =
    Boolean(hasCabinet) && !bootstrap.currentUser.roles.includes('customer');
  const isSubscriptionOwner = isSubscriptionOwnerForTab;
  const profileContent = (
    <>
      {transferToken ? (
        <TransferAcceptanceSection
          bootstrap={bootstrap}
          token={transferToken}
          onSessionExpired={onSessionExpired}
        />
      ) : null}

      <div className="container-style">
        <form onSubmit={handleSaveIdentity} id="your-profile">
          <div className="user-profile-picture">
          <ProfileAvatar
            src={avatarPreviewUrl || profile?.avatar_url}
            alt={profile?.display_name || __('Avatar utilisateur')}
            className="profile-picture__img"
          />
          {editIconUrl ? (
            <label htmlFor="profile_picture_upload" className="profile-picture__button">
              <img
                src={editIconUrl}
                alt={__('Modifier la photo')}
                className="profile_picture_upload_icon"
              />
              <input
                ref={fileInputRef}
                type="file"
                id="profile_picture_upload"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => void onAvatarFileChange(e)}
              />
            </label>
          ) : (
            <label htmlFor="profile_picture_upload" className="profile-picture__button button">
              {__('Modifier la photo')}
              <input
                ref={fileInputRef}
                type="file"
                id="profile_picture_upload"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => void onAvatarFileChange(e)}
              />
            </label>
          )}
          </div>
        {avatarError ? <p className="accounting-hub-notice accounting-hub-notice-error">{avatarError}</p> : null}

        <div className="form-container user-profile-info">
          <div className="user-profile-info-row">
            <div>
              <label htmlFor="first_name">{__('Prénom')}</label>
              <input
                id="first_name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="last_name">{__('Nom')}</label>
              <input
                id="last_name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
          <div>
            <label htmlFor="email">{__('Email')}</label>
            <input id="email" type="email" value={profile?.email ?? ''} readOnly />
          </div>
          <div className="field-checkbox">
            <input
              id="product_updates_opt_in"
              type="checkbox"
              checked={productUpdatesOptIn}
              onChange={(e) => setProductUpdatesOptIn(e.target.checked)}
            />
            <label htmlFor="product_updates_opt_in" className="field-checkbox-label">
              {__('Restez informé sur les prochaines mises à jour')}
            </label>
          </div>
        </div>

        {showCabinetProfileFields ? (
          <div className="form-container user-profile-cabinet">
            <h4>{__('Cabinet')}</h4>
            {!profile?.can_edit_cabinet ? (
              <FeatureGateNotice decision={cabinetProfileEditGate} />
            ) : null}
            <div>
              <label htmlFor="cabinet_name">{__('Nom du cabinet')}</label>
              {profile?.can_edit_cabinet ? (
                <input
                  id="cabinet_name"
                  type="text"
                  value={cabinetName}
                  onChange={(e) => setCabinetName(e.target.value)}
                />
              ) : (
                <p className="cabinet-readonly">{cabinetName || '—'}</p>
              )}
            </div>
          </div>
        ) : null}

        <div className="user-profile-actions">
          <button type="submit" className="btn-primary" disabled={nameSaving}>
            {nameSaving ? __('Enregistrement...') : __('Enregistrer')}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowPasswordFields((v) => !v)}
            aria-expanded={showPasswordFields}
          >
            {showPasswordFields ? __('Masquer') : __('Modifier le mot de passe')}
          </button>
        </div>
        {nameMessage ? <p aria-live="polite">{nameMessage}</p> : null}
      </form>

        {showPasswordFields && (
          <div className="form-container user-profile-password-section">
            <form
              onSubmit={handleSavePassword}
              className="user-profile-password"
              aria-labelledby="password-heading"
            >
              <div id="password-heading" className="visually-hidden">
                {__('Changer le mot de passe')}
              </div>
              <div>
                <label htmlFor="current_password">{__('Mot de passe actuel')}</label>
                <input
                  id="current_password"
                  type="password"
                  autoComplete="current-password"
                  className="password-field"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="user-profile-password-row">
                <div>
                  <label htmlFor="new_password">{__('Nouveau mot de passe')}</label>
                  <input
                    id="new_password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    className="password-field"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <span className="description">{__('Minimum 8 caractères')}</span>
                  <div
                    id="password-strength"
                    className={`password-strength ${passwordStrength.className}`.trim()}
                  >
                    {passwordStrength.label}
                  </div>
                </div>
                <div>
                  <label htmlFor="confirm_password">{__('Confirmer')}</label>
                  <input
                    id="confirm_password"
                    type="password"
                    autoComplete="new-password"
                    className="password-field"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <span
                    id="password-match-message"
                    className={`password-match-message ${passwordMatch.className}`.trim()}
                  >
                    {passwordMatch.text}
                  </span>
                </div>
              </div>
              <button type="submit" className="btn-primary user-profile-password-submit" disabled={passwordSaving}>
                {passwordSaving ? __('Mise à jour...') : __('Changer le mot de passe')}
              </button>
              {passwordMessage ? <p aria-live="polite">{passwordMessage}</p> : null}
              {passwordError ? (
                <p className="accounting-hub-notice accounting-hub-notice-error">{passwordError}</p>
              ) : null}
            </form>
          </div>
        )}

        <TwoFactorManagementSection bootstrap={bootstrap} />
      </div>
    </>
  );

  const profileTabPanels = [
    <TabPanel key="profile" id="profile" label={__('Profil')}>
      {profileContent}
    </TabPanel>,
  ];

  if (hasNotificationsTab) {
    profileTabPanels.push(
      <TabPanel key="notifications" id="notifications" label={__('Notifications')}>
        <NotificationsTab bootstrap={bootstrap} onSessionExpired={onSessionExpired} />
      </TabPanel>,
    );
  }

  if (isSubscriptionOwner) {
    profileTabPanels.push(
      <TabPanel key="advanced" id="advanced" label={__('Avancé')}>
        <AdvancedTab bootstrap={bootstrap} onSessionExpired={onSessionExpired} />
      </TabPanel>,
    );
  }

  return (
    <div id="custom-profile-page">
      <h2 className="h4">{__('Mon Profil')}</h2>
      {useProfileTabs ? (
        portalTabs ? (
          <Tabs
            activeTab={profileTabShell}
            defaultActiveTab="profile"
            onActiveTabChange={(id) =>
              patchPortalSearch({ tab: id === 'profile' ? null : id }, 'push')
            }
          >
            {profileTabPanels}
          </Tabs>
        ) : (
          <Tabs defaultActiveTab={profileTabShell}>
            {profileTabPanels}
          </Tabs>
        )
      ) : (
        <>
          {profileContent}
          {bootstrap.isGdprModuleEnabled ? (
            <div className="container-style" style={{ marginTop: '24px' }}>
              <PrivacyDataSection bootstrap={bootstrap} onSessionExpired={onSessionExpired} />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ProfilePage;
